import React, { useState } from 'react'
import FormikImageUpload from '../../../../components/Formik/FormikImageUpload'
import { useToast } from '../../../../components/toast/ToastContext';
import { useDocumnetPreSignedUrlMutation } from '../../../../api/contactApiSlice';
import { Formik } from 'formik';
import StyledText from '../../../../components/StyledComponents/StyledText';
import { useTheme } from '../../../../middleware/ThemeContext';
import styled from 'styled-components';
import { font, Theme } from '../../../../utils/themes';
import { WebsiteFormValues } from '../../../../types/types';
import { useLogoPreSignedUrlMutation } from '../../../../api/pageBuilderApiSlice';
import { SkeletonCell } from '../../../../components/SkeletonCell';

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}
const FavIconCard = ({ companyLoading, isResponsive, setSettingConfiguration, settingConfiguration }: { companyLoading?: boolean, isResponsive?: boolean, setSettingConfiguration: React.Dispatch<React.SetStateAction<WebsiteFormValues>>, settingConfiguration: WebsiteFormValues }) => {
    const { theme } = useTheme()
    const [favIconPreSignedUrl] = useLogoPreSignedUrlMutation();
    const showToast = useToast();
    const [hasUploaded, setHasUploaded] = useState<boolean>(false);
    const [fileNameErr, setFileNameErr] = useState<string>('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    const [uploadKey, setUploadKey] = useState(0);



    const validateFavIcon = (file: UploadedFile): Promise<boolean> => {
        return new Promise((resolve) => {

            // 🔴 Size check
            if (file.file.size > 1 * 1024 * 1024) {
                showToast("Error", "Favicon size must be less than 1MB", "error");
                resolve(false);
                return;
            }

            const img = new Image();
            img.src = URL.createObjectURL(file.file);

            img.onload = () => {
                const { width, height } = img;

                if (
                    width < 32 ||
                    height < 32 ||
                    width > 64 ||
                    height > 64
                ) {
                    showToast(
                        "Error",
                        "Favicon dimensions must be between 32px and 64px",
                        "error"
                    );
                    resolve(false);
                } else {
                    resolve(true);
                }
            };

            img.onerror = () => {
                showToast("Error", "Invalid image file", "error");
                resolve(false);
            };
        });
    };



    const handleUpload = async (
        file: UploadedFile,
        onProgress?: (percent: number) => void
    ): Promise<{ key: string }> => {
        const parts = file.name?.split(".");
        const extension = parts.pop();



        try {
            const { data } = await favIconPreSignedUrl({
                fileName: file.name,
                fileType: extension,
            });

            if (!data?.url) {
                showToast("Error", "Could not generate upload URL", "error");
                return { key: '' };
            }

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open(data.method, data.url, true);
                xhr.setRequestHeader("Content-Type", file.type);

                // 👇 Add progress tracking
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        onProgress(percent);
                    }
                };

                xhr.onload = () => {
                    if (xhr.status === 200 || xhr.status === 204) {
                        resolve({ key: data.key });
                    } else {
                        reject(new Error("Upload failed"));
                    }
                };

                xhr.onerror = () => reject(new Error("Network error"));
                xhr.send(file.file);
            });
        } catch (err) {
            showToast("Error", "Upload preparation failed", "error");
            return { key: '' };
        }
    };

    return (
        <div className=' p-3 rounded-3 w-100' style={{ border: `1px solid ${theme.CorporateTheme.colors.border}` }}>
            <Formik
                initialValues={{
                    file: [],
                    faviconPreview: settingConfiguration?.faviconPreview || '',
                }}
                enableReinitialize
                // validationSchema={setScopeSchema}
                onSubmit={(values) => {
                    console.log(values);
                }}
            >
                {({ handleSubmit, values, setFieldValue }) => {
                    const handleFileChange = async (files: UploadedFile[]) => {
                        if (!files.length) return;
                        const file = files[0];
                        // 🔴 STOP FLOW if invalid
                        const isValid = await validateFavIcon(file);
                        if (!isValid) {
                            setHasUploaded(false);
                            setUploadProgress(0);
                            setIsUploading(false);

                            // 🔥 RESET EVERYTHING
                            setFieldValue('file', []);
                            setFieldValue('favicon', '');
                            setFieldValue('faviconPreview', '');

                            setSettingConfiguration(prev => ({
                                ...prev,
                                favicon: '',
                                faviconPreview: ''
                            }));

                            // 🔥 FORCE FormikImageUpload RESET
                            setUploadKey(prev => prev + 1);

                            return;
                        }


                        const reader = new FileReader();
                        reader.onload = () => {
                            if (reader.result) {
                                setFieldValue('faviconPreview', reader.result);
                                // setWebsiteDetails(prev => ({ ...prev, logoPreview: reader.result as string }));
                                setSettingConfiguration(prev => ({ ...prev, faviconPreview: reader.result as string }));
                            }
                        };
                        reader.readAsDataURL(file.file);

                        setIsUploading(true);
                        setUploadProgress(0);

                        handleUpload(file, setUploadProgress)
                            .then(({ key }) => {
                                if (key) {
                                    setFieldValue('favicon', key);
                                    setSettingConfiguration(prev => ({ ...prev, favicon: key, faviconFileName: file.name, faviconFileSize: file.file.size }));
                                    setHasUploaded(true);
                                }
                            })
                            .catch(() => {
                                showToast('Error', 'Failed to upload favicon', 'error');
                            })
                            .finally(() => {
                                setIsUploading(false);
                            });
                    };

                    const handleRemoveLogo = () => {
                        setFieldValue('favicon', '');
                        setFieldValue('faviconPreview', '');
                        setHasUploaded(false);
                        // Update parent state to send null in payload
                        setSettingConfiguration(prev => ({
                            ...prev,
                            favicon: '',
                            faviconPreview: '',
                            faviconFileName: '',
                            faviconFileSize: 0
                        }));
                    };
                    return (
                        <div>
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <StyledText fontSize={isResponsive ? '16px' : '18px'} fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>Favicon</StyledText>
                                <div className='d-flex align-items-center gap-1'>
                                    <StyledText fontStyle='italic' fontSize={isResponsive ? '12px' : '14px'} fontWeight={700} textColor={theme.CorporateTheme.colors.count}>Recommended : </StyledText>
                                    {companyLoading ? <SkeletonCell height={isResponsive ? '20px' : '25px'} width='70px' /> : <FavIconRecommendationCard theme={theme}>
                                        <StyledText fontStyle='italic' fontFamily={font.lato} fontSize='12px' fontWeight={700} textColor={theme.CorporateTheme.colors.headerColor}>32-64px</StyledText>

                                    </FavIconRecommendationCard>}
                                    {companyLoading ? <SkeletonCell height={isResponsive ? '20px' : '25px'} width='100px' /> : <FavIconRecommendationCard theme={theme}>
                                        <StyledText fontStyle='italic' fontFamily={font.lato} fontSize='12px' fontWeight={700} textColor={theme.CorporateTheme.colors.headerColor}>Less Then 1MB</StyledText>

                                    </FavIconRecommendationCard>}
                                </div>
                            </div>
                            {companyLoading ? <SkeletonCell height={'132px'} width='100%' /> : <FormikImageUpload accept=".png, .jpg, .jpeg, .webp" key={uploadKey} existingFileName={settingConfiguration?.fileName} existingFileSize={settingConfiguration?.fileSize} showRenameInput={true} name='file' onChange={handleFileChange} setHasUploaded={setHasUploaded} previewUrl={values.faviconPreview} uploadProgress={uploadProgress}
                                isUploading={isUploading}
                                removeFile={handleRemoveLogo} checkError={fileNameErr} isResponsive={isResponsive} />}

                        </div>
                    )
                }}
            </Formik>
        </div>
    )
}

const FavIconRecommendationCard = styled.div<{ theme: Theme }>`
    height: 30px;
    background: ${({ theme }) => theme.CorporateTheme.colors.headerBG}; 
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0px 15px;
`

export default FavIconCard
